import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";

import { useGetSocieties } from "../../../../api/queries";
import Select from "../../../../components/Select";
import CrossIcon from "../../../../components/svg/CrossIcon";
import {
  PlusIconMinus,
  PlusIconPlus,
} from "../../../../components/svg/PlusIcon";
import TickIcon from "../../../../components/svg/TickIcon";
import Table from "../../../../components/Table";
import WriterAmendmentsPill from "../../../../components/WriterAmendmentsPill";
import useGetColours from "../../../../helpers/getColours";
import WriterDetails from "./WriterDetails";

function RightToCollectCell({ value, isTunecore, statusSuccessColour, statusWarningColour }) {
  return (
    <span>
      {value && value > 0 ? (
        <div
          style={{ backgroundColor: statusSuccessColour }}
          className="flex items-center justify-center w-8 h-8 p-2 rounded-lg"
        >
          <TickIcon className="w-6 h-6" fill={isTunecore ? "black" : "white"} />
        </div>
      ) : (
        <div
          style={{
            backgroundColor: !isTunecore ? statusWarningColour : "#EE9E26",
          }}
          className="flex items-center justify-center w-8 h-8 p-2 rounded-lg"
        >
          <CrossIcon className="w-6 h-6" fill={isTunecore ? "black" : "white"} />
        </div>
      )}
    </span>
  );
}

function AmendmentCell({ row }) {
  return (
    <div>
      <WriterAmendmentsPill object={row.original} />
    </div>
  );
}

function ExpanderCell({ row, isTunecore, highlightColour }) {
  return (
    <>
      {isTunecore ? (
        <span className="float-right">
          {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      ) : (
        <span className="float-right">
          {row.isExpanded ? (
            <PlusIconMinus className="h-4" fill={highlightColour} />
          ) : (
            <PlusIconPlus className="h-4" fill={highlightColour} />
          )}
        </span>
      )}
    </>
  );
}

function RightToCollectCellWrapper({ isTunecore, statusSuccessColour, statusWarningColour }) {
  return props => (
    <RightToCollectCell
      {...props}
      isTunecore={isTunecore}
      statusSuccessColour={statusSuccessColour}
      statusWarningColour={statusWarningColour}
    />
  );
}

function ExpanderCellWrapper({ isTunecore, highlightColour }) {
  return props => (
    <ExpanderCell
      {...props}
      isTunecore={isTunecore}
      highlightColour={highlightColour}
    />
  );
}

// Our table component
function WritersTable({ data, highlightColour }) {
  const { t } = useTranslation("writers");
  const societies = useGetSocieties();

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const [filterOption, setFilterOption] = useState("all");
  const { statusSuccessColour, statusWarningColour } = useGetColours();

  const RightToCollectCellWithProps = React.useMemo(
    () => RightToCollectCellWrapper({ isTunecore, statusSuccessColour, statusWarningColour }),
    [isTunecore, statusSuccessColour, statusWarningColour],
  );

  const ExpanderCellWithProps = React.useMemo(
    () => ExpanderCellWrapper({ isTunecore, highlightColour }),
    [isTunecore, highlightColour],
  );

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        Header: t("writers:writer-name-col"),
        accessor: ({ writername }) => writername,
        width: "20%",
        id: "colWriterTableName",
      },
      {
        Header: t("writers:pro-membership-col"),
        accessor: ({ societycisaccode }) => {
          if (!societycisaccode) {
            return null;
          }
          if (societies.data) {
            const pro = societies.data.societies.find(
              s => s.cisaccode === societycisaccode,
            );
            return pro?.name;
          }
          return null;
        },
        width: "20%",
        id: "colWriterTablePRO",
      },
      {
        Header: "CAE/IPI#",
        accessor: ({ caeipinumber }) => caeipinumber,
        width: "15%",
        id: "colWriterTableCAE",
      },
      {
        Header: t("writers:right-to-collect-col"),
        accessor: ({ writercontrolledworkscount }) =>
          writercontrolledworkscount,
        width: "15%",
        id: "colWriterTableRTC",
        Cell: RightToCollectCellWithProps,
      },
      {
        Header: t("writers:writer-amendment-status"),
        accessor: ({ verifiedstate }) => verifiedstate,
        width: "20%",
        id: "colVerifiedState",
        Cell: AmendmentCell,
      },
      {
        Header: () => null,
        id: "expander",
        width: "5%",
        Cell: ExpanderCellWithProps,
        SubCell: () => null,
      },
    ];

    if (isTunecore) {
      baseColumns.splice(1, 0, {
        Header: t("writers:dob"),
        accessor: ({ dateofbirth }) => {
          const dateString = dateofbirth;
          const formattedDate = moment.utc(dateString).format("YYYY-MM-DD");
          if (!dateString) {
            return null;
          }
          return formattedDate;
        },
        width: "15%",
        id: "colWriterTableDOB",
      });
    }

    return baseColumns;
  }, [societies.data, isTunecore, t, ExpanderCellWithProps, RightToCollectCellWithProps]);

  const generateWriterName = (person) => {
    let fullName = `${person.firstname} ${person.lastname}`;

    if (person?.middlename) {
      fullName = `${person.firstname} ${person.middlename} ${person.lastname}`;
    }

    return fullName;
  };

  const addedWriterNameField = data.map(person => ({
    ...person,
    writername: generateWriterName(person),
  }));

  // Filter data based on selected filter option
  const filteredData = addedWriterNameField.filter((writer) => {
    if (filterOption === "rtc") {
      return writer.writercontrolledworkscount > 0;
    }
    if (filterOption === "no-rtc") {
      return writer.writercontrolledworkscount === 0;
    }
    return true; // "all" option
  });

  // Options for the Select component
  const selectOptions = [
    { value: "all", name: t("writers:all-writers") },
    { value: "rtc", name: t("writers:writers-with-rtc") },
    { value: "no-rtc", name: t("writers:writers-with-no-rtc") },
  ];

  return (
    <>
      <Table
        area="WritersTable"
        columns={columns}
        data={filteredData}
        revealComponent={<WriterDetails highlightColour={highlightColour} />}
        highlightColour={highlightColour}
        searchSuffix={t("common:writers")}
        filtersSection={(
          <Select
            list
            id="writer-filter"
            options={selectOptions}
            value={filterOption}
            onChange={value => setFilterOption(value)}
            placeholder={t("writers:select-filter")}
            className={`${
              isTunecore ? "tunecore-select" : "select-grey-bg"
            } select-search`}
            name="writer-filter"
          />
        )}
      />
    </>
  );
}

export default WritersTable;
